import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';

// Below-fold sections loaded lazily
const PowerTubeDeepDive = lazy(() => import('./components/PowerTubeDeepDive'));
const VideoShowcase = lazy(() => import('./components/VideoShowcase'));
const UrbanLifestyle = lazy(() => import('./components/UrbanLifestyle'));
const PowerMeetsStyle = lazy(() => import('./components/PowerMeetsStyle'));
const TechSpecs = lazy(() => import('./components/TechSpecs'));
const PowerTubeSpecs = lazy(() => import('./components/PowerTubeSpecs'));
const Community = lazy(() => import('./components/Community'));
const Footer = lazy(() => import('./components/Footer'));

// Minimal fallback – invisible so layout doesn't jump
const SectionFallback = () => <div className="min-h-[200px] bg-black" />;

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://indmobility.com';
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'IND Mobility',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/c396747a877dd1d0cb2c362594545533/02f73f35a156274691f368b3b9fff544.png',
        description: 'IND - The Future of E-mobility',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'IND Mobility',
        url: siteUrl,
        description: 'IND - The Future of E-mobility',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/product/{search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'IND Mobility - Powering Your Independence',
        description: 'IND - The Future of E-mobility',
        url: siteUrl,
        isPartOf: { '@type': 'WebSite', name: 'IND Mobility', url: siteUrl },
      },
    ];

    const scriptTags = schemas.map((schema) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => document.head.removeChild(el));
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navLinks = [
    { label: 'TECHNOLOGY', href: '#tech' },
    { label: 'POWERTUBE', href: '#powertube' },
    { label: 'LIFESTYLE', href: '#lifestyle' },
    { label: 'COMMUNITY', href: '#community' },
  ];

  const handleMobileNavClick = (href: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-12">
            <a href="/" aria-label="IND Mobility Home">
              <img 
                src="https://static.readdy.ai/image/c396747a877dd1d0cb2c362594545533/02f73f35a156274691f368b3b9fff544.png" 
                alt="IND Mobility" 
                width="48"
                height="48"
                className="h-12 w-auto"
              />
            </a>
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="text-sm font-medium hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap">{link.label}</a>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center">
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-xl"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="flex flex-col items-center justify-center w-6 h-5 relative">
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute ${mobileMenuOpen ? 'rotate-45 top-1/2 -translate-y-1/2' : 'top-0'}`}></span>
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute top-1/2 -translate-y-1/2 ${mobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}></span>
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute ${mobileMenuOpen ? '-rotate-45 top-1/2 -translate-y-1/2' : 'bottom-0'}`}></span>
            </div>
          </button>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 bg-black/95 backdrop-blur-md transition-all duration-500 md:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className={`flex flex-col items-center justify-center h-full transition-transform duration-500 ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-8'}`}>
          <div className="flex flex-col items-center space-y-8 mb-12">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleMobileNavClick(link.href); }}
                className="text-3xl font-black tracking-tight hover:text-teal-400 transition-all duration-300 cursor-pointer"
                style={{ transitionDelay: mobileMenuOpen ? `${index * 80}ms` : '0ms', opacity: mobileMenuOpen ? 1 : 0, transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)' }}
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="flex flex-col items-center space-y-4" style={{ transitionDelay: mobileMenuOpen ? '320ms' : '0ms', opacity: mobileMenuOpen ? 1 : 0, transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.3s ease' }}>
            <button className="text-lg font-medium hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap" onClick={() => { setMobileMenuOpen(false); setComingSoonOpen(true); }}>SHOP</button>
            <button
              onClick={() => { setMobileMenuOpen(false); setComingSoonOpen(true); }}
              className="bg-teal-500 text-black px-8 py-3 rounded-full text-base font-bold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
            >TEST DRIVE</button>
          </div>
        </div>
      </div>

      <main id="main-content">
        <Hero />

        {/* Coming Soon Modal */}
        {comingSoonOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center px-6"
            onClick={() => setComingSoonOpen(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <div
              className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setComingSoonOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl"></i>
              </button>

              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-500/10 border border-teal-500/30 mx-auto mb-5">
                <i className="ri-time-line text-2xl text-teal-400"></i>
              </div>

              <h2 className="text-2xl font-black tracking-tight mb-2">COMING SOON</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Test drives are launching shortly. Stay tuned — we&apos;ll let you know the moment bookings open.
              </p>

              <button
                onClick={() => setComingSoonOpen(false)}
                className="w-full bg-teal-500 text-black py-3 rounded-full font-bold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                GOT IT
              </button>
            </div>
          </div>
        )}

        <Suspense fallback={<SectionFallback />}>
          <PowerTubeDeepDive />
          <VideoShowcase />
          <UrbanLifestyle />
          <PowerMeetsStyle />
          <TechSpecs />
          <PowerTubeSpecs />
          <Community />
          <Footer />
        </Suspense>
      </main>
    </div>
  );
}
